import { ACHIEVEMENTS, getAchievement, } from '../data/achievements.js';
import { getShopTitle } from '../data/shop-titles.js';
import { BIOME_IDS } from '../data/biomes.js';
import { pagesAtTier } from './bestiary.js';
import { totalKills } from './career.js';
function careerStat(career, stat) {
    return stat === 'kills' ? totalKills(career) : career[stat];
}
/** Every goal but the completions, which count the others. */
function measure(achievement, facts) {
    const { goal } = achievement;
    switch (goal.kind) {
        case 'kills':
            return [goal.mobs.reduce((sum, mob) => sum + (facts.career.kills[mob] ?? 0), 0), goal.count];
        case 'portal':
            return [facts.portals.includes(goal.portal) ? 1 : 0, 1];
        case 'hidden-chest':
            return [facts.hiddenChests.includes(goal.biome) ? 1 : 0, 1];
        case 'camp':
            return [facts.career.camps[goal.biome] ?? 0, goal.count];
        case 'career':
            return [careerStat(facts.career, goal.stat), goal.count];
        case 'beta':
            return [facts.betaAccount ? 1 : 0, 1];
        case 'bestiary':
            return [pagesAtTier(facts.career, goal.tier), goal.pages];
        case 'complete':
        case 'complete-all':
            return [0, 0];
    }
}
function entry(value, target) {
    return { value: Math.min(value, target), target, done: value >= target };
}
/** Where every achievement stands, keyed by id, in the order of the data. */
export function achievementProgress(facts) {
    const progress = new Map();
    for (const achievement of ACHIEVEMENTS) {
        const kind = achievement.goal.kind;
        if (kind === 'complete' || kind === 'complete-all')
            continue;
        const [value, target] = measure(achievement, facts);
        progress.set(achievement.id, entry(value, target));
    }
    const completed = new Set();
    for (const achievement of ACHIEVEMENTS) {
        const { goal } = achievement;
        if (goal.kind !== 'complete')
            continue;
        const others = ACHIEVEMENTS.filter((other) => other.biome === goal.biome && other.goal.kind !== 'complete');
        const done = others.filter((other) => progress.get(other.id)?.done === true).length;
        const result = entry(done, others.length);
        if (result.done)
            completed.add(goal.biome);
        progress.set(achievement.id, result);
    }
    for (const achievement of ACHIEVEMENTS) {
        if (achievement.goal.kind !== 'complete-all')
            continue;
        progress.set(achievement.id, entry(completed.size, BIOME_IDS.length));
    }
    return progress;
}
export function doneAchievements(progress) {
    return [...progress].filter(([, state]) => state.done).map(([id]) => id);
}
/** Achievements done now that were not before: what the unlock notice names. */
export function newlyDone(before, after) {
    return doneAchievements(after).filter((id) => before.get(id)?.done !== true);
}
/**
 * A title may be worn once the achievement that gives it is done, or while
 * the account keeps the offer that gives it (`purchases`, offer ids); null
 * takes it off.
 */
export function canWearTitle(progress, achievementId, purchases = []) {
    if (achievementId === null)
        return true;
    const shopTitle = getShopTitle(achievementId);
    if (shopTitle !== undefined)
        return purchases.includes(shopTitle.offer);
    return (getAchievement(achievementId)?.title !== undefined && progress.get(achievementId)?.done === true);
}
/** Whether an achievement's item may be claimed now; null when it may. */
export function rewardRefusal(progress, achievementId, claimed) {
    const achievement = getAchievement(achievementId);
    if (achievement === undefined)
        return 'unknown';
    if (achievement.reward === undefined)
        return 'no-reward';
    if (progress.get(achievementId)?.done !== true)
        return 'not-done';
    if (claimed.has(achievementId))
        return 'claimed';
    return null;
}
//# sourceMappingURL=achievements.js.map